import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

/* A route change should land at the top of the new page, and a link carrying
   a hash (#cars) at the section it names.

   A changed query string is neither. The inventory filters live in the URL,
   so every box ticked is a navigation — and treating those as a new page
   threw the visitor back to the hero on each one. Only a different path
   scrolls to the top; the rest is the same page, still under them.

   The hash is the awkward one, for two reasons. The section it names may not
   be mounted yet — the home page's grid arrives with the catalogue — and once
   it is, other things on the page are still moving: PageTransition fades the
   new page in and refreshes ScrollTrigger on the way out, and a refresh puts
   the scroll position back where it measured it. A single scrollIntoView is
   simply undone. So the section is held at the top for a beat instead, and
   the first touch from the visitor ends the hold — nobody should have to
   fight the page for their own scroll wheel. */

/* Long enough to outlast the page transition's refresh, short enough that a
   visitor who immediately scrolls barely notices it. */
const HOLD_MS = 700

function ScrollToTop() {
  const { pathname, hash } = useLocation()
  const previous = useRef<string | null>(null)

  useEffect(() => {
    const changedPage = previous.current !== pathname
    previous.current = pathname

    if (!hash) {
      if (changedPage) {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
      }
      return
    }

    let frame = 0
    let released = false
    const until = performance.now() + HOLD_MS

    const release = () => {
      released = true
    }

    const scroll = () => {
      if (released) return

      const target = document.getElementById(hash.slice(1))
      if (target) target.scrollIntoView({ behavior: 'instant', block: 'start' })

      if (performance.now() < until) frame = requestAnimationFrame(scroll)
    }

    frame = requestAnimationFrame(scroll)

    /* Anything the visitor does to move the page themselves wins. */
    const options = { passive: true } as const
    window.addEventListener('wheel', release, options)
    window.addEventListener('touchstart', release, options)
    window.addEventListener('keydown', release, options)

    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('wheel', release)
      window.removeEventListener('touchstart', release)
      window.removeEventListener('keydown', release)
    }
  }, [pathname, hash])

  return null
}

export default ScrollToTop
